// Achievement badge artwork, ported from design/hot-type/boards/badges.js (the approved drawings).
// One emblem per category, struck in bronze, silver and gold (gold carries a moving shine).
// Finishing a category sets it Ablaze: the emblem turns to fire, yellow to red and flowing
// upward, in a hot glow with embers drifting off it. Every emblem is drawn on a 128 grid.
// badgeSvg() returns an <svg> string; `simple` (the default at 32px and below, such as beside a
// name on the leaderboard) sizes the emblem up and drops the glow, and Ablaze's embers there are
// fewer and bigger. The gradients the badges paint with (and the bear's drawing) live in one shared
// <defs> SVG that ensureBadgeDefs() puts in <body>; the motion and the locked colours are in app.css
// ("Achievement badges"). Ablaze's glow and embers aren't in the drawing: they're HTML under and over
// it (emberLayer), which the GPU moves on its own; inside the <svg>, every frame of them repainted
// the whole badge. Locked badges paint through gradients whose stops read the theme's
// --badge-locked-* colours, so they follow a theme switch without a re-render; a ghost badge (a
// locked one on a blue card) reads --badge-ghost-* the same way.

import { setFire } from './badge-motion.ts'
import { BEAR_ART } from './bear-art.ts'

export type BadgeCategory = 'cub' | 'regular' | 'archivist' | 'headliner' | 'wordsmith' | 'firekeeper' | 'rival' | 'legend'
export type BadgeTier = 'locked' | 'bronze' | 'silver' | 'gold' | 'blaze'

export const BADGE_CATEGORIES: readonly BadgeCategory[] = ['cub', 'regular', 'archivist', 'headliner', 'wordsmith', 'firekeeper', 'rival', 'legend']
export const BADGE_TIERS: readonly BadgeTier[] = ['locked', 'bronze', 'silver', 'gold', 'blaze']

export const BADGE_CATEGORY_NAMES: Record<BadgeCategory, string> = {
  cub: 'Cub Reporter',
  regular: 'The Regular',
  archivist: 'The Archivist',
  headliner: 'Headliner',
  wordsmith: 'Wordsmith',
  firekeeper: 'Firekeeper',
  rival: 'The Rival',
  legend: 'Legend',
}
export const BADGE_TIER_NAMES: Record<BadgeTier, string> = { locked: 'Locked', bronze: 'Bronze', silver: 'Silver', gold: 'Gold', blaze: 'Ablaze' }

// The paints an emblem is drawn with: base, deep (recessed), line (engraving), hi (highlight),
// edge (the embossed rim under it) and gem (the crown's ruby).
type Paint = { base: string; deep: string; edge: string; line: string; hi: string; gem: string }

const METALS = {
  bronze: { base: ['#7a4520', '#c27f45', '#f6c793', '#b8743a', '#6e3d1a'], deep: ['#5a3014', '#8e5528', '#5f3414'], edge: '#3a1f0a', line: '#4a2810', hi: '#ffe4c4' },
  silver: { base: ['#6d737a', '#c3c8ce', '#ffffff', '#a9afb6', '#5f656c'], deep: ['#4a5057', '#878d94', '#50565d'], edge: '#26292d', line: '#3a3f45', hi: '#ffffff' },
  gold: { base: ['#8f6204', '#deab2f', '#fff2b0', '#d29b16', '#7f5603'], deep: ['#634102', '#a27206', '#694402'], edge: '#3a2600', line: '#5a3a00', hi: '#fffbe6' },
}
// Ablaze paints with the animated fire gradients in the shared defs.
const BLAZE = { edge: '#3a0800', line: '#6e1206', hi: '#fff8d0' }
const RUBY = 'url(#bm-ruby)'
const themed = (set: string): Paint => ({ base: `url(#bm-${set}-base)`, deep: `url(#bm-${set}-deep)`, edge: `url(#bm-${set}-edge)`, line: `url(#bm-${set}-line)`, hi: 'none', gem: `url(#bm-${set}-deep)` })
const LOCKED = themed('locked')
const GHOST = themed('ghost')

const f = (n: number) => Math.round(n * 100) / 100
const rad = (d: number) => (d * Math.PI) / 180
const pt = (cx: number, cy: number, r: number, a: number): [number, number] => [f(cx + r * Math.cos(rad(a))), f(cy + r * Math.sin(rad(a)))]
const path = (d: string, fill: string, more = '') => (fill === 'none' ? '' : `<path d="${d}" fill="${fill}" ${more}/>`)
const line = (d: string, P: Paint, w = 1.6, more = '') => (P.line === 'none' ? '' : `<path d="${d}" fill="none" stroke="${P.line}" stroke-width="${w}" stroke-linecap="round" stroke-linejoin="round" ${more}/>`)
const hi = (d: string, P: Paint, w = 2.2, op = 0.6) => (P.hi === 'none' ? '' : `<path d="${d}" fill="none" stroke="${P.hi}" stroke-width="${w}" stroke-linecap="round" opacity="${op}"/>`)
const star4 = (x: number, y: number, s: number) => `M${x} ${y - s} C${f(x + s * 0.16)} ${f(y - s * 0.16)} ${f(x + s * 0.16)} ${f(y - s * 0.16)} ${x + s} ${y} C${f(x + s * 0.16)} ${f(y + s * 0.16)} ${f(x + s * 0.16)} ${f(y + s * 0.16)} ${x} ${y + s} C${f(x - s * 0.16)} ${f(y + s * 0.16)} ${f(x - s * 0.16)} ${f(y + s * 0.16)} ${x - s} ${y} C${f(x - s * 0.16)} ${f(y - s * 0.16)} ${f(x - s * 0.16)} ${f(y - s * 0.16)} ${x} ${y - s} Z`
const star5 = (cx: number, cy: number, R: number, r: number, rot = -90) => Array.from({ length: 10 }, (_, i) => pt(cx, cy, i % 2 ? r : R, rot + i * 36))
const poly = (pts: [number, number][]) => `M${pts.map((p) => p.join(' ')).join(' L')} Z`

// ── Emblems

// Cub Reporter: a grizzly on the move, traced from the drawing in design/hot-type/assets/bear by
// tools/vectorize-bear.py. Its light fur is the metal, its shaded fur the recessed paint (the
// darkest of it deepened toward the edge colour), and its outline and details the edge colour.
// The drawing is big (34 KB of paths), so it's in the shared defs once and each badge <use>s it.
const bearPart = (part: keyof typeof BEAR_ART, fill: string, more = '') => (fill === 'none' ? '' : `<use href="#bm-bear-${part}" fill="${fill}" ${more}/>`)
function bear(P: Paint) {
  // In one colour (the embossed copy, the shine's mask) the fur inside adds nothing.
  if (P.base === P.deep && P.deep === P.edge) return bearPart('interior', P.base) + bearPart('ink', P.base)
  return bearPart('interior', P.deep) + bearPart('deepest', P.edge, 'opacity=".45"') + bearPart('light', P.base) + bearPart('ink', P.edge)
}

// The Regular: a wall clock at ten past ten, for the players who are always on time.
function clock(P: Paint) {
  const stroke = (d: string, w: number) => `<path d="${d}" fill="none" stroke="${P.base}" stroke-width="${w}" stroke-linecap="round"/>`
  const dot = (x: number, y: number, r: number, fill: string) => `<circle cx="${x}" cy="${y}" r="${r}" fill="${fill}"/>`
  let ticks = ''
  for (let i = 0; i < 12; i++) {
    const a = i * 30 - 90
    if (i % 3 === 0) {
      const [x1, y1] = pt(64, 62, 25, a)
      const [x2, y2] = pt(64, 62, 32, a)
      ticks += stroke(`M${x1} ${y1} L${x2} ${y2}`, 5.5)
    } else {
      const [x, y] = pt(64, 62, 30, a)
      ticks += dot(x, y, 2.3, P.base)
    }
  }
  const [hx, hy] = pt(64, 62, 17, 215)
  const [mx, my] = pt(64, 62, 25, -30)
  const [ax, ay] = pt(64, 62, 40.5, 195)
  const [bx, by] = pt(64, 62, 40.5, 250)
  return dot(64, 62, 44, P.base) + dot(64, 62, 36.5, P.deep) +
    hi(`M${ax} ${ay} A40.5 40.5 0 0 1 ${bx} ${by}`, P, 2.2, 0.6) +
    ticks + stroke(`M64 62 L${hx} ${hy}`, 7) + stroke(`M64 62 L${mx} ${my}`, 5.2) +
    dot(64, 62, 5, P.base) + dot(64, 62, 1.8, P.deep) + (P.hi === 'none' ? '' : `<circle cx="62.4" cy="60.4" r="1.2" fill="${P.hi}" opacity=".6"/>`)
}

// The Archivist: a messy stack of folded back issues, today's front page on top.
function stack(P: Paint) {
  const slab = (x: number, y: number, w: number, rot: number, cx: number, cy: number) => `<g transform="rotate(${rot} ${cx} ${cy})">` +
    path(`M${x + 5} ${y} h${w - 5} v10 h-${w - 5} a5 5 0 0 1 -5 -5 a5 5 0 0 1 5 -5 Z`, P.base, P.line === 'none' ? '' : `stroke="${P.edge}" stroke-width="1.2"`) +
    line(`M${x + 6} ${y + 3.6} H${x + w - 3}`, P, 0.9, 'opacity=".45"') + line(`M${x + 6} ${y + 6.6} H${x + w - 3}`, P, 0.9, 'opacity=".45"') +
    path(`M${x + 4} ${y + 8.6} h${w - 4} v1.4 h-${w - 4} Z`, P.deep) + `</g>`
  return slab(22, 87, 84, 0, 64, 92) + slab(25, 77, 82, 2.5, 64, 82) + slab(19, 67, 84, -3, 64, 72) + slab(25, 58, 80, 1, 64, 63) +
    path('M33 27 L95 27 L104 59 L24 59 Z', P.base, P.line === 'none' ? '' : `stroke="${P.edge}" stroke-width="1.2"`) +
    path('M38.5 31.5 L89.5 31.5 L91.4 38.5 L36.6 38.5 Z', P.deep) +
    path('M36 42.5 L76 42.5 L76.8 46 L35 46 Z', P.deep) +
    path('M34.4 49.5 L55 49.5 L55.6 55.5 L32.8 55.5 Z', P.deep) +
    line('M60 50.3 L96 50.3', P, 1.2, 'opacity=".55"') + line('M60.5 53 L97 53', P, 1.2, 'opacity=".55"') + line('M61 55.7 L94 55.7', P, 1.2, 'opacity=".55"') +
    hi('M35 29.6 L93 29.6', P, 1.4, 0.55)
}

// Headliner: a bevelled star shooting up the page, trailing headline bars.
function shootingStar(P: Paint) {
  const s = star5(76, 46, 33, 14)
  const d = [-0.7071, 0.7071]
  const n = [0.7071, 0.7071]
  let trails = ''
  for (const [k, len, w] of [[-11, 20, 6], [0, 34, 8], [11, 20, 6]]) {
    const x1 = f(76 + d[0] * 26 + n[0] * k), y1 = f(46 + d[1] * 26 + n[1] * k)
    const x2 = f(x1 + d[0] * len), y2 = f(y1 + d[1] * len)
    trails += P.deep === 'none' ? '' : `<path d="M${x1} ${y1} L${x2} ${y2}" stroke="${k ? P.deep : P.base}" stroke-width="${w}" stroke-linecap="round"/>`
  }
  let facets = ''
  for (let i = 0; i < 10; i++) facets += path(poly([[76, 46], s[i], s[(i + 1) % 10]]), i % 2 ? P.deep : P.base)
  return trails + path(poly(s), P.base) + facets + (P.hi === 'none' ? '' : `<circle cx="76" cy="46" r="2.6" fill="${P.hi}" opacity=".8"/>`)
}

// Wordsmith: a hammer coming down on an anvil, sparks flying.
function anvil(P: Paint) {
  return path('M20 56 C29 56 37 58 44 60 L101 60 C103 60 105 62 105 64 L105 68 C105 70 103 72 101 72 L87 72 C82 74 80 79 80 85 L90 92 C92 93 93 95 93 98 L35 98 C35 95 36 93 38 92 L48 85 C48 79 46 74 41 72 C31 70 24 64 20 56 Z', P.base) +
    path('M50.5 75 L77.5 75 C76.5 78 76 81.5 76.5 86 L51.5 86 C52 81.5 51.5 78 50.5 75 Z', P.deep) +
    path('M37 93 L91 93 L92.5 98 L35.5 98 Z', P.deep) +
    hi('M45 62.4 L100 62.4', P, 2, 0.6) + hi('M24 58.5 C31 60 37 61.5 43 62.6', P, 1.6, 0.5) +
    `<g transform="rotate(-32 60 46)">` +
      path('M72 43 h33 a3 3 0 0 1 0 6 h-33 Z', P.deep) +
      path('M46 38 h28 a3 3 0 0 1 3 3 v10 a3 3 0 0 1 -3 3 h-28 a3 3 0 0 1 -3 -3 v-10 a3 3 0 0 1 3 -3 Z', P.base) +
      path('M43 41 a3 3 0 0 1 3 -3 h4 v16 h-4 a3 3 0 0 1 -3 -3 Z', P.deep) +
      hi('M53 40.5 L73 40.5', P, 1.6, 0.6) +
    `</g>` +
    path(star4(34, 44, 6), P.base) + path(star4(45, 33, 4), P.base) + path(star4(26, 35, 3.2), P.base)
}

// Firekeeper: a three-tongued flame with a hot core.
function flame(P: Paint) {
  return path('M66 13 C70 27 84 35 90 49 C94 45 95 39 94 33 C102.5 45 104.5 62 99.5 76 C94.5 92 81 104 64 104 C46 104 32 92 29 76 C26 62 30 50 38 41 C38 49 41 55 46 58 C44 43 52 27 66 13 Z', P.base) +
    path('M64 43 C68 56 81.5 63 81.5 80 C81.5 92 73 99.5 64 99.5 C55 99.5 46.5 92 46.5 82 C46.5 74 51 68.5 56 65.5 C56 71.5 58 75.5 62 76.5 C59 66.5 59 54.5 64 43 Z', P.deep, P.line === 'none' ? '' : `stroke="${P.line}" stroke-width="1" stroke-opacity=".5"`) +
    path('M64 69 C66 76.5 73.5 80.5 73.5 88 C73.5 94 69 97.5 64 97.5 C59 97.5 54.5 94 54.5 88 C54.5 82.5 59 78.5 64 69 Z', P.base) +
    hi('M37 50 C31.5 60 31 74 35.5 85', P, 2.4, 0.55) + hi('M64.5 21 C61 30 57 37 55 46', P, 1.8, 0.45)
}

// The Rival: two fountain pens crossed like swords, a spark where the nibs meet.
function pens(P: Paint) {
  const pen = (sep: boolean) => {
    const ring = sep && P.line !== 'none' ? `stroke="${P.edge}" stroke-width="2.6" paint-order="stroke"` : ''
    return path('M64 9 C68 18 70.5 28 70.5 40 L57.5 40 C57.5 28 60 18 64 9 Z', P.base, ring) +
      line('M64 19 L64 33.5', P, 1.4) + `<circle cx="64" cy="34.5" r="1.8" fill="${P.deep}"/>` +
      path('M56.5 40 h15 a1.5 1.5 0 0 1 1.5 1.5 v4 a1.5 1.5 0 0 1 -1.5 1.5 h-15 a1.5 1.5 0 0 1 -1.5 -1.5 v-4 a1.5 1.5 0 0 1 1.5 -1.5 Z', P.deep, ring) +
      path('M57.5 47 h13 v58 a6.5 6.5 0 0 1 -13 0 Z', P.base, ring) +
      path('M57.5 88 h13 v4 h-13 Z', P.deep) +
      hi('M60.5 50 L60.5 85', P, 1.6, 0.55)
  }
  return `<g transform="rotate(-38 64 62)">${pen(false)}</g><g transform="rotate(38 64 62)">${pen(true)}</g>` +
    path(star4(64, 19, 9), P.base) + (P.hi === 'none' ? '' : `<path d="${star4(64, 19, 4)}" fill="${P.hi}" opacity=".8"/>`)
}

// Legend: a crown whose points are flames, a ruby in the band.
function crown(P: Paint) {
  const tongue = (x: number, y: number, h: number, w: number) => `M${x} ${y} C${f(x - w)} ${f(y - h * 0.32)} ${f(x - w * 0.3)} ${f(y - h * 0.64)} ${x} ${y - h} C${f(x + w * 0.3)} ${f(y - h * 0.64)} ${f(x + w)} ${f(y - h * 0.32)} ${x} ${y} Z`
  return path(tongue(33, 61, 23, 8.5), P.base) + path(tongue(64, 49, 33, 11), P.base) + path(tongue(95, 61, 23, 8.5), P.base) +
    path(tongue(33, 60, 12, 4.2), P.deep) + path(tongue(64, 48, 18, 5.5), P.deep) + path(tongue(95, 60, 12, 4.2), P.deep) +
    path('M30 90 L33 57 L49 71 L64 45 L79 71 L95 57 L98 90 Z', P.base) +
    path('M39.5 84 L41.5 67.5 L50.5 75.5 L64 55 L77.5 75.5 L86.5 67.5 L88.5 84 Z', P.deep) +
    path('M26 86 Q64 80 102 86 L102 99 Q64 93 26 99 Z', P.base) +
    line('M27 89.5 Q64 83.5 101 89.5', P, 1.2, 'opacity=".6"') +
    `<circle cx="64" cy="91" r="6" fill="${P.gem || RUBY}" ${P.line === 'none' ? '' : `stroke="${P.edge}" stroke-width="1.4"`}/>` +
    `<circle cx="44" cy="92.6" r="3" fill="${P.deep}"/><circle cx="84" cy="92.6" r="3" fill="${P.deep}"/>` +
    (P.hi === 'none' ? '' : `<circle cx="62" cy="89" r="1.8" fill="#fff" opacity=".75"/>`) +
    hi('M35 86 L37 62', P, 1.8, 0.5)
}

const EMBLEMS: Record<BadgeCategory, (P: Paint) => string> = { cub: bear, regular: clock, archivist: stack, headliner: shootingStar, wordsmith: anvil, firekeeper: flame, rival: pens, legend: crown }

// Ablaze sits in a hot glow that breathes in and out (app.css draws it: a circle of r 60 at 64, 62).
const glowHot = '<badge-glow aria-hidden="true"></badge-glow>'

/** An ember: x, y, radius, sideways drift, seconds, delay, on the 128 grid. */
export type Ember = readonly [number, number, number, number, number, number]
/**
 * Embers as HTML over a badge of `size` px, each rising `rise` and shrinking to `end` as it fades.
 * `corner`: shrink toward the grid's corner as the drawings' CSS did (the emblems' approved drift),
 * not about the ember's own centre (the Phoenix's). `cls` picks the timing and glow in app.css.
 */
export function emberLayer(list: readonly Ember[], size: number, { rise, end, colour, corner, cls }: { rise: number; end: number; colour(i: number): string; corner: boolean; cls: string }): string {
  const k = size / 128
  const pct = (n: number) => f((n / 128) * 100)
  return `<badge-embers class="${cls}" aria-hidden="true">${list.map(([x, y, r, dx, dur, delay], i) => {
    const tx = corner ? dx - (1 - end) * x : dx
    const ty = corner ? -rise - (1 - end) * y : -rise
    return `<badge-ember style="left:${pct(x - r)}%;top:${pct(y - r)}%;width:${pct(2 * r)}%;height:${pct(2 * r)}%;background:${colour(i)};--tx:${f(tx * k)}px;--ty:${f(ty * k)}px;--end:${end};animation-duration:${dur}s;animation-delay:${delay}s"></badge-ember>`
  }).join('')}</badge-embers>`
}

// Embers drift up off a burning badge.
const EMBERS: readonly Ember[] = [[36, 84, 3, -7, 3.1, 0], [50, 92, 2.2, 5, 2.6, 0.8], [64, 80, 3.4, -3, 3.4, 1.6], [78, 92, 2.4, 8, 2.8, 0.4], [92, 84, 3, 4, 3.2, 2.1], [44, 70, 2, -9, 2.4, 1.2], [86, 72, 2.2, 7, 2.9, 2.6], [70, 64, 1.8, -5, 2.5, 3], [28, 60, 1.8, -6, 2.7, 1.9], [100, 58, 2, 6, 3, 0.2]]
const embers = (size: number) => emberLayer(EMBERS, size, { rise: 64, end: 0.35, colour: (i) => (i % 3 ? '#ff8a2a' : '#ffe08a'), corner: true, cls: 'blaze' })
// Small badges (beside a name) get fewer, bigger embers that drift farther, so they read at 20px.
const EMBERS_MINI: readonly Ember[] = [[44, 84, 12, -16, 2.3, 0], [72, 78, 13, 14, 2.7, 0.8], [58, 66, 11, -8, 2.1, 1.5], [86, 90, 12, 18, 2.5, 2.1]]
const embersMini = (size: number) => emberLayer(EMBERS_MINI, size, { rise: 170, end: 0.3, colour: (i) => (i % 2 ? '#ff8a2a' : '#ffe08a'), corner: true, cls: 'blaze mini' })

export type BadgeSvgOptions = {
  /** Rendered width and height in px. Default 128. */
  size?: number
  /** The small-size drawing: emblem sized up, no glow, Ablaze's embers fewer and bigger. Default: size <= 32. */
  simple?: boolean
  /** Leave the label off and hide the SVG from assistive tech, for a wrapper that carries the label. */
  decorative?: boolean
  /** Locked only: drawn in blue, for a blue card where the locked grey would vanish. */
  ghost?: boolean
}

let uidN = 0
export function badgeSvg(category: BadgeCategory, tier: BadgeTier, { size = 128, simple = size <= 32, decorative = false, ghost = false }: BadgeSvgOptions = {}): string {
  const emblem = EMBLEMS[category]
  const uid = `bdg${++uidN}`
  const locked = tier === 'locked'
  const P: Paint = locked ? (ghost ? GHOST : LOCKED)
    : tier === 'blaze' ? { base: 'url(#bm-blaze-base)', deep: 'url(#bm-blaze-deep)', line: BLAZE.line, hi: BLAZE.hi, edge: BLAZE.edge, gem: RUBY }
    : { base: `url(#bm-${tier}-base)`, deep: `url(#bm-${tier}-deep)`, line: METALS[tier].line, hi: METALS[tier].hi, edge: METALS[tier].edge, gem: RUBY }
  const solid = (c: string): Paint => ({ base: c, deep: c, line: 'none', hi: 'none', edge: c, gem: c })
  const s = simple ? 1.14 : tier === 'blaze' ? 0.92 : 1
  const withMount = (paint: Paint) => `<g transform="translate(64 62) scale(${s}) translate(-64 -62)">${emblem(paint)}</g>`
  // The emblem is embossed: a copy in the edge colour sits 2.4 below it.
  const body = `<g transform="translate(0 2.4)">${withMount(solid(P.edge))}</g>` + withMount(P)
  // Gold's shine sweeps across, clipped to the emblem: a pause, then across in 1.2 s, every 3 s. It's
  // SMIL, so badge-motion.ts's clock can move it (the timeline is paused there).
  const shines = tier === 'gold'
  const mask = shines ? `<mask id="${uid}m" maskUnits="userSpaceOnUse" x="0" y="0" width="128" height="128">${withMount(solid('#fff'))}</mask>` : ''
  const shine = shines ? `<g mask="url(#${uid}m)"><g class="b-shine"><animateTransform attributeName="transform" type="translate" values="0 0;0 0;200 0;200 0" keyTimes="0;.4;.8;1" calcMode="spline" keySplines="0 0 1 1;.42 0 .58 1;0 0 1 1" dur="3s" repeatCount="indefinite"/><rect x="-60" y="-20" width="44" height="168" transform="rotate(18 64 64)" fill="url(#bm-shine)"/></g></g>` : ''
  let back = '', front = ''
  if (tier === 'blaze') {
    if (simple) front = embersMini(size)
    else { back = glowHot; front = embers(size) }
  }
  const cls = `ach-badge t-${tier}${simple ? ' simple' : ''}`
  const a11y = decorative ? 'aria-hidden="true" focusable="false"' : `role="img" aria-label="${BADGE_CATEGORY_NAMES[category]}, ${BADGE_TIER_NAMES[tier]}"`
  // The glow and embers are HTML around the <svg>, for a box that is the badge's size (Badge.tsx).
  return `${back}<svg class="${cls}" width="${size}" height="${size}" viewBox="0 0 128 128" ${a11y}>${body}${mask}${shine}</svg>${front}`
}

// The shared gradients every badge paints with, put in <body> once. Ablaze's two flow upward:
// badge-motion.ts slides them (reflected, they repeat every 192) while a burning badge is in sight.
export function ensureBadgeDefs(): void {
  if (typeof document === 'undefined' || document.getElementById('badge-defs')) return
  const grad = (id: string, stops: string[], offs: number[]) => `<linearGradient id="${id}" x1="18" y1="12" x2="110" y2="116" gradientUnits="userSpaceOnUse">${stops.map((c, i) => `<stop offset="${offs[i]}" stop-color="${c}"/>`).join('')}</linearGradient>`
  let g = ''
  for (const [k, m] of Object.entries(METALS)) g += grad(`bm-${k}-base`, m.base, [0, 0.34, 0.5, 0.64, 1]) + grad(`bm-${k}-deep`, m.deep, [0, 0.5, 1])
  const themedPaint = (set: string, k: string) => `<linearGradient id="bm-${set}-${k}" x1="0" y1="0" x2="128" y2="0" gradientUnits="userSpaceOnUse"><stop offset="0" style="stop-color:var(--badge-${set}-${k})"/></linearGradient>`
  for (const set of ['locked', 'ghost']) g += ['base', 'deep', 'edge', 'line'].map((k) => themedPaint(set, k)).join('')
  const el = document.createElement('div')
  el.id = 'badge-defs'
  el.style.cssText = 'position:absolute;width:0;height:0;overflow:hidden'
  const bearArt = Object.entries(BEAR_ART).map(([k, d]) => `<path id="bm-bear-${k}" d="${d}"/>`).join('')
  el.innerHTML = `<svg width="0" height="0" aria-hidden="true"><defs>${g}${bearArt}
    <radialGradient id="bm-ruby" cx="45%" cy="38%" r="65%"><stop offset="0" stop-color="#ffb2a0"/><stop offset=".45" stop-color="#e5492d"/><stop offset="1" stop-color="#7a1a0a"/></radialGradient>
    <linearGradient id="bm-blaze-base" x1="0" y1="112" x2="0" y2="16" gradientUnits="userSpaceOnUse" spreadMethod="reflect"><stop offset="0" stop-color="#fff6c2"/><stop offset=".35" stop-color="#ffc53a"/><stop offset=".7" stop-color="#ff6a1a"/><stop offset="1" stop-color="#d4260c"/></linearGradient>
    <linearGradient id="bm-blaze-deep" x1="0" y1="112" x2="0" y2="16" gradientUnits="userSpaceOnUse" spreadMethod="reflect"><stop offset="0" stop-color="#ffb03a"/><stop offset=".35" stop-color="#e8540f"/><stop offset=".7" stop-color="#a8200a"/><stop offset="1" stop-color="#6e1206"/></linearGradient>
    <linearGradient id="bm-shine" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset=".5" stop-color="#fff" stop-opacity=".9"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>
  </defs></svg>`
  document.body.prepend(el)
  // The fire flows (badge-motion.ts moves these two) only while a burning badge is in sight.
  setFire([...el.querySelectorAll<SVGGradientElement>('#bm-blaze-base, #bm-blaze-deep')])
}
